import React from 'react'
import _ from 'lodash'

import { EntityRenderer } from 'shared-libs/components/entity/renderer'

import { ComponentsContext } from 'browser/contexts/components/components-context'
import { PlatformType } from 'shared-libs/models/types/storyboard/storyboard-plan'
import { followLink } from 'browser/app/utils/utils'
import { StoryboardTask } from 'shared-libs/models/types/storyboard/storyboard-task'
import { EventType } from 'shared-libs/models/types/storyboard/storyboard-execution'
import printJS from 'print-js'
import { Mappings } from 'shared-libs/models/types/storyboard/storyboard-plan-model'
import { ErrorModal } from '../error-modal'

interface IStoryRendererProps {
  task: StoryboardTask
  currentRoute: any
  currentNode: any
  entity: any
  taskEntity: any
  taskState?: any

  /**
   * Schema / server validation errors for `taskEntity`, keyed by dot-joined
   * value path. Painted per-field by the `ui2:*` shells.
   */
  errors?: any
  /**
   * Flipped by the host once the user has attempted to advance. Until then the
   * renderer keeps UI-required errors out of the per-field error map so empty
   * required fields don't glow before the first save attempt.
   */
  hasSubmitted?: boolean
  /**
   * Receives the UI-driven required-field errors (and their labels) the
   * renderer collects from `isRequired` fields, so the host can gate advance.
   */
  onValidate?: (errors: Record<string, string[]>, labels?: Record<string, string>) => void

  navigateForwards: () => void
  navigateBackwards: () => void
  updateExecutionState: (mappings: Mappings, context: any) => void
  onChange: () => void
  actions?: any
}

interface IStoryRendererState {}

export class StoryRenderer extends React.Component<IStoryRendererProps, IStoryRendererState> {
  static contextType = ComponentsContext
  context!: React.ContextType<typeof ComponentsContext>
  onEntityChange: any

  constructor(props) {
    super(props)

    this.onEntityChange = _.debounce(this._onEntityChange, 250)
  }

  public render() {
    const { task, taskEntity, actions, entity, errors, hasSubmitted, onValidate, taskState } =
      this.props
    const isMobileWeb = this.context.platform === PlatformType.MOBILE_WEB

    if (_.includes([EventType.CREATE, EventType.EDIT, EventType.DETAIL], task.actionType)) {
      const taskUiSchema = task.uiSchema(this.context.platform)
      const settings = this.props.entity.api.settings

      return (
        <div>
          {!_.isEmpty(taskUiSchema) && (
            <EntityRenderer
              componentsMap={this.context.components}
              uiSchema={taskUiSchema}
              uiSchemaPath="ignoreMe"
              errors={errors}
              hasSubmitted={hasSubmitted}
              onChangeComplete={this.onEntityChange}
              onValidate={onValidate}
              value={taskEntity}
              actions={actions}
              state={{
                ...taskState,
                settings,
                actionHooks: this.getActionHooks(),
                self: entity
              }}
              context={
                isMobileWeb
                  ? {
                      density: 'collapse',
                      isFullScreen: true,
                      isHorizontalLayout: false,
                    }
                  : undefined
              }
            />
          )}
        </div>
      )
    }

    return null
  }

  private getActionHooks() {
    return {
      goNext: this.props.navigateForwards,
      goBack: this.props.navigateBackwards,
      print: this.handlePrint,
      updateExecutionState: this.props.updateExecutionState,
      openLink: followLink,
    }
  }

  private _onEntityChange = () => {
    this.props.onChange()
  }

  /**
   * Handle printing of a file url
   * @param fileUrl  file url
   * @param onClose a callback when the diaglog is closed
   */
  private handlePrint = (fileUrl: string, onClose: () => void, onError?: () => void): Promise<void> => {
    return new Promise((resolve) => {
      return printJS({
        printable: fileUrl,
        onError: async (error, xmlHttpRequest) => {
          ErrorModal.open({
            errorTitle: 'Unable to print',
            errorText: error,
          })
          console.error('Unable to print', error, xmlHttpRequest)
          await onError?.()
          resolve()
        },
        onPrintDialogClose: async () => {
          await onClose()
          resolve()
        },
      })
    })
  }
}
